import React, { useState, useRef, useEffect } from "react";
import { MdArrowBack } from "react-icons/md";
import { Link } from "react-router-dom";
import { Avatar, VStack } from "rsuite";
import { toast } from "react-toastify";
import { axiosApi, serverUrl } from "config";
import { useDispatch, useSelector } from "react-redux";
import { updateUser } from "store/slices/auth";

const AccountSettings = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  // Refs for inputs
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const currentPasswordRef = useRef(null); // New ref for Current Password

  // Local state initialized from Redux store
  const [serverAvatarUrl, setServerAvatarUrl] = useState(
    user?.photo ? `${serverUrl}${user.photo}` : "https://bit.ly/default-avatar"
  );

  const [previewAvatarUrl, setPreviewAvatarUrl] = useState(null);

  const [name, setName] = useState(user?.name || "");
  const [editName, setEditName] = useState(false);

  const [email, setEmail] = useState(user?.email || "");
  const [editEmail, setEditEmail] = useState(false);

  const [password, setPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [editPassword, setEditPassword] = useState(false);

  const [twoFactorAuth, setTwoFactorAuth] = useState(user?.twoFactorAuth || false);

  // Update local state when user.photo changes
  useEffect(() => {
    if (user?.photo) {
      if (user.photo.startsWith("http")) {
        // It's already a full URL (e.g., from Google)
        setServerAvatarUrl(user.photo);
      } else {
        // It's a relative path from our server
        setServerAvatarUrl(`${serverUrl}${user.photo}`);
      }
      setPreviewAvatarUrl(null);
    } else {
      setServerAvatarUrl("https://bit.ly/default-avatar");
    }
  }, [user?.photo]);
  

  // Trigger focus on input when editing
  useEffect(() => {
    if (editName && nameRef.current) nameRef.current.focus();
  }, [editName]);

  useEffect(() => {
    if (editEmail && emailRef.current) emailRef.current.focus();
  }, [editEmail]);

  useEffect(() => {
    if (editPassword && currentPasswordRef.current) currentPasswordRef.current.focus(); // Focus on Current Password
  }, [editPassword]);

  // Toggle name field
  const handleNameToggle = async () => {
    if (editName) {
      try {
        const res = await axiosApi.put("/settings/user/profile", { name });
        toast.success("Name updated successfully");

        if (res.data?.user) {
          dispatch(updateUser(res.data.user));
        } else {
          dispatch(updateUser({ name }));
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to update name");
      }
    }
    setEditName(!editName);
  };

  // Toggle email field
  const handleEmailToggle = async () => {
    if (editEmail) {
      try {
        const res = await axiosApi.put("/settings/user/profile", { email });
        toast.success("Email updated successfully");

        if (res.data?.user) {
          dispatch(updateUser(res.data.user));
        } else {
          dispatch(updateUser({ email }));
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to update email");
      }
    }
    setEditEmail(!editEmail);
  };

  // Toggle password field
  const handlePasswordToggle = async () => {
    if (editPassword) {
      if (!currentPassword || !password) {
        toast.warn("Please provide both current and new password");
        return;
      }
      try {
        const res = await axiosApi.put("/settings/user/password", {
          currentPassword,
          newPassword: password,
        });
        toast.success(res.data.message || "Password updated successfully");
        setCurrentPassword("");
        setPassword("");
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to update password");
      }
    }
    setEditPassword(!editPassword);
  };

  // Handle photo selection
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Set local preview
    const previewUrl = URL.createObjectURL(file);
    setPreviewAvatarUrl(previewUrl);

    // Prepare form data
    const formData = new FormData();
    formData.append("photo", file);

    try {
      const res = await axiosApi.post("/settings/user/photo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message);

      if (res.data?.user) {
        dispatch(updateUser(res.data.user));
      } else {
        // Assuming the server returns the photo path
        dispatch(updateUser({ photo: res.data.photo }));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update photo");
      setPreviewAvatarUrl(null); // Revert to server avatar on failure
    }
  };

  // Toggle 2FA
  const handle2FA = async () => {
    try {
      const res = await axiosApi.post("/settings/user/2fa", {
        twoFactorAuth: !twoFactorAuth,
      });
      toast.success("2FA preference updated");

      if (res.data?.user) {
        dispatch(updateUser(res.data.user));
      } else {
        dispatch(updateUser({ twoFactorAuth: res.data?.twoFactorAuth || !twoFactorAuth }));
      }
      setTwoFactorAuth(res.data?.twoFactorAuth || !twoFactorAuth);
    } catch (err) {
      toast.error(err.response?.data?.error || "Error updating 2FA");
    }
  };

  // Logout all devices
  const handleLogoutAll = async () => {
    try {
      const res = await axiosApi.post("/settings/user/logout-all");
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || "Error logging out all devices");
    }
  };

  return (
    <div className="mt-4">
      <Link to="/main/profile">
        <div className="flex gap-4 text-purple-800 hover:text-purple-500 items-center font-bold">
          <MdArrowBack className="h-4 w-4 flex" />
          <p>Back to Settings</p>
        </div>
      </Link>
      <div className="mt-4">
        {/* Profile Info */}
        <div className="flex flex-col rounded-lg bg-gradient-to-b from-gray-50 to-purple-50 p-4 text-left shadow-md">
          <h4>Profile Information</h4>
          <VStack direction="column" spacing={3} className="mt-8 max-w-fit">
            <Avatar
              name={user?.name || "User Name"}
              src={previewAvatarUrl || serverAvatarUrl}
              className="!rounded-full w-[150px] h-[150px] object-cover"
              circle
            />
            <input
              type="file"
              id="avatar-upload"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <button
              onClick={() => document.getElementById("avatar-upload").click()}
              className="mt-3 text-left font-bold text-purple-800 hover:text-purple-500"
            >
              Change Photo
            </button>
          </VStack>

          {/* Name Field */}
          <div className="border-b border-gray-200 pb-2">
            <div className="mt-6 flex justify-start gap-10">
              <div>
                <p>Name</p>
                <input
                  type="text"
                  ref={nameRef}
                  className={`mt-2 rounded-md py-2 ${
                    editName ? "!bg-white" : "bg-purple-50"
                  } w-full min-w-[300px] focus:outline-none focus:ring-1 focus:ring-purple-500`}
                  disabled={!editName}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  aria-label="Name"
                />
              </div>
              <button
                className="flex text-purple-800 hover:text-purple-500 font-bold"
                onClick={handleNameToggle}
              >
                {editName ? "Save" : "Edit"}
              </button>
            </div>
          </div>

          {/* Email Field */}
          <div className="pb-2">
            <div className="mt-6 flex justify-start gap-10">
              <div>
                <p>Email</p>
                <input
                  type="email"
                  ref={emailRef}
                  className={`mt-2 rounded-md py-2 ${
                    editEmail ? "!bg-white" : "bg-purple-50"
                  } w-full min-w-[300px] focus:outline-none focus:ring-1 focus:ring-purple-500`}
                  disabled={!editEmail}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-label="Email"
                />
              </div>
              <button
                className="flex text-purple-800 hover:text-purple-500 font-bold"
                onClick={handleEmailToggle}
              >
                {editEmail ? "Save" : "Edit"}
              </button>
            </div>
          </div>
        </div>

        {/* Password Management */}
        <div className="flex flex-col rounded-lg bg-gradient-to-b from-gray-50 to-purple-50 p-4 text-left shadow-md mt-4">
          <h4>Password Management</h4>
          <div className="border-b border-gray-200 pb-2">
            {editPassword && (
              <div className="mt-6 flex justify-start gap-10">
                <div>
                  <p>Current Password</p>
                  <input
                    type="password"
                    ref={currentPasswordRef} // Assign the new ref here
                    className="mt-2 rounded-md py-2 !bg-white w-full min-w-[300px] focus:outline-none focus:ring-1 focus:ring-purple-500"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    aria-label="Current Password"
                  />
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-start gap-10">
              <div>
                <p>New Password</p>
                <input
                  type="password"
                  ref={passwordRef}
                  className={`rounded-md py-2 ${
                    editPassword ? "!bg-white" : "bg-gray-50"
                  } w-full min-w-[300px] focus:outline-none focus:ring-1 focus:ring-purple-500`}
                  disabled={!editPassword}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-label="New Password"
                />
              </div>
              <button
                className="flex whitespace-nowrap text-purple-800 hover:text-purple-500 font-bold"
                onClick={handlePasswordToggle}
              >
                {editPassword ? "Save" : "Change Password"}
              </button>
            </div>
          </div>

          {/* 2FA Toggle */}
          <div className="pb-2">
            <div className="mt-6 flex justify-start gap-10">
              <div className="w-full max-w-[300px] whitespace-nowrap">
                <p>Two-factor authentication (2FA)</p>
              </div>
              <button
                className="flex text-purple-800 hover:text-purple-500 font-bold"
                onClick={handle2FA}
              >
                {twoFactorAuth ? "Disable" : "Setup"}
              </button>
            </div>
          </div>

          {/* Logout from all devices */}
          <div className="pb-2">
            <div className="mt-6 flex justify-start gap-10">
              <div className="w-full max-w-[300px] whitespace-nowrap">
                <p>Log activity</p>
              </div>
              <button
                className="flex whitespace-nowrap text-purple-800 hover:text-purple-500 font-bold"
                onClick={handleLogoutAll}
              >
                Log out of all devices
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
